import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Search } from 'lucide-react'
import PageHero from '../components/PageHero'
import { posts } from '../data/content'

const categories = ['Todos', ...new Set(posts.map((post) => post.category))]

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Todos')

  const filteredPosts = useMemo(() => posts.filter((post) => {
    const query = searchTerm.trim().toLowerCase()
    const haystack = [
      post.title,
      post.excerpt,
      ...post.sections.flatMap((section) => [section.heading, ...section.paragraphs]),
    ].join(' ').toLowerCase()
    const matchesSearch = !query || haystack.includes(query)
    const matchesCategory = selectedCategory === 'Todos' || post.category === selectedCategory
    return matchesSearch && matchesCategory
  }), [searchTerm, selectedCategory])

  return (
    <div className="min-h-screen bg-ivory">
      <PageHero
        eyebrow="Seis lecturas"
        title="La bitácora"
        highlight="del campo"
        description="Historia, reglas, equipo y los nombres que ya compiten. Sin relleno de un blog genérico de golf."
      />

      <div className="border-b border-leaf/10 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <label className="relative w-full max-w-md">
            <span className="sr-only">Buscar artículos</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-mist" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar por tema o nombre"
              className="w-full rounded-full border border-leaf/15 bg-ivory py-3 pl-11 pr-4 text-sm outline-none ring-leaf focus:ring-2"
            />
          </label>
          <div className="flex gap-2 overflow-x-auto" role="group" aria-label="Filtrar por tema">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                aria-pressed={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
                className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold ${
                  selectedCategory === category ? 'bg-leaf text-white' : 'bg-cream text-mist hover:text-leaf'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        {filteredPosts.length === 0 ? (
          <p className="py-16 text-center text-lg text-mist">Ningún artículo coincide con esa búsqueda.</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post) => (
              <article key={post.slug} className="flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-leaf/10">
                <div className="flex items-center justify-between gap-3 text-xs font-semibold uppercase tracking-wide">
                  <span className="text-leaf">{post.category}</span>
                  <span className="text-mist">{post.readTime}</span>
                </div>
                <h2 className="mt-5 font-serif text-2xl leading-snug">
                  <Link to={`/blog/${post.slug}`} className="hover:text-leaf">
                    {post.title}
                  </Link>
                </h2>
                <p className="mt-3 flex-1 leading-relaxed text-mist">{post.excerpt}</p>
                <Link to={`/blog/${post.slug}`} className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-leaf">
                  Leer
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Blog
